// The die as the roll button: the same solid that tumbles on the stage, small.
// While it waits for the roll it rocks from side to side. Under the pointer it
// turns and its rings of dither show. After a roll it lies with the rolled
// face up, small and with no color, as a record. When it becomes the roll
// again it grows and turns once. While the die on the stage tumbles, this one is away:
// the die left the button.

import {
  REDUCED_MOTION_QUERY,
  ROLL_DIE_GLOW_RINGS,
  ROLL_DIE_HOVER_SPIN,
  ROLL_DIE_RADIUS_RATIO,
  ROLL_DIE_REST_RADIUS_RATIO,
  ROLL_DIE_ROCK,
  ROLL_DIE_ROCK_MS,
  ROLL_DIE_SPIN_EASE,
  ROLL_DIE_TILT,
  ROLL_DIE_WAKE_SPIN,
} from '../constants';
import { type DieFaceLabel, landingOrientation } from '../dice/octahedron';
import { axisAngle, type Mat3, multiply } from '../dice/vector';
import { setAttributes, svg } from '../lib/svg';
import { createDieSolid } from './DieSolid';
import { createTonePattern } from './ditherTone';

export type RollDieMode = 'ready' | 'rest' | 'away';

export type RollDie = {
  svg: SVGSVGElement;
  /** `face` is the last roll. With none, the die shows its first face. */
  show: (mode: RollDieMode, face: number | null) => void;
  /** What each face reads. */
  label: (labels: readonly DieFaceLabel[]) => void;
  /** The pointer is on the button. */
  excite: (excited: boolean) => void;
};

const TONE_ID_PREFIX = 'roll-die-tone-';
const GLOW_ID_PREFIX = 'roll-die-glow-';
const FIRST_FACE = 0;
const ACROSS: readonly [number, number, number] = [1, 0, 0];
const UPRIGHT: readonly [number, number, number] = [0, 1, 0];
const MS_PER_SECOND = 1000;

/** The die leans back and is turned by `turn` about the upright axis of the screen. */
const waitingPose = (turn: number): Mat3 =>
  multiply(
    axisAngle(ACROSS, -ROLL_DIE_TILT),
    multiply(axisAngle(UPRIGHT, turn), landingOrientation(FIRST_FACE)),
  );

export const createRollDie = (): RollDie => {
  const solid = createDieSolid(TONE_ID_PREFIX);
  const rings = ROLL_DIE_GLOW_RINGS.map((_, index) =>
    svg('circle', {
      class: `roll-die__ring roll-die__ring--${index}`,
      fill: `url(#${GLOW_ID_PREFIX}${index})`,
    }),
  );
  // The widest ring is first, so each ring lies on the wider ones.
  const glow = svg('g', { class: 'roll-die__glow' }, [...rings].reverse());
  for (const [index, ring] of ROLL_DIE_GLOW_RINGS.entries()) {
    solid.defs.appendChild(
      createTonePattern(`${GLOW_ID_PREFIX}${index}`, ring.litCells, 'roll-die__spark', null),
    );
  }
  const root = svg('svg', { class: 'roll-die', 'aria-hidden': 'true' }, [
    solid.defs,
    glow,
    solid.group,
  ]);
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

  let mode: RollDieMode = 'rest';
  let face: number | null = null;
  let excited = false;
  let frame = 0;
  let lastFrameAt = 0;
  let rockClock = 0;
  let spin = 0;
  let spinSpeed = 0;

  const draw = (orientation: Mat3): void => {
    // One unit of the drawing is one screen pixel, so the dither cells stay whole.
    const side = root.clientWidth;
    if (side === 0) return;
    const center = side / 2;
    const radius = center * (mode === 'ready' ? ROLL_DIE_RADIUS_RATIO : ROLL_DIE_REST_RADIUS_RATIO);
    setAttributes(root, { viewBox: `0 0 ${side} ${side}` });
    rings.forEach((ring, index) => {
      const reach = ROLL_DIE_GLOW_RINGS[index]?.reach ?? 1;
      setAttributes(ring, { cx: center, cy: center, r: radius * reach });
    });
    solid.draw({ x: center, y: center, scale: 1, orientation }, radius);
  };

  const drawStill = (): void => {
    draw(mode === 'rest' && face !== null ? landingOrientation(face) : waitingPose(0));
  };

  const step = (now: number): void => {
    const elapsed = lastFrameAt === 0 ? 0 : (now - lastFrameAt) / MS_PER_SECOND;
    lastFrameAt = now;
    rockClock += elapsed;
    const wanted = excited ? ROLL_DIE_HOVER_SPIN : 0;
    spinSpeed += (wanted - spinSpeed) * Math.min(1, elapsed * ROLL_DIE_SPIN_EASE);
    spin += spinSpeed * elapsed;
    const rock =
      ROLL_DIE_ROCK * Math.sin((rockClock * MS_PER_SECOND * 2 * Math.PI) / ROLL_DIE_ROCK_MS);
    draw(waitingPose(spin + rock));
    frame = requestAnimationFrame(step);
  };

  const stop = (): void => {
    cancelAnimationFrame(frame);
    frame = 0;
    lastFrameAt = 0;
  };

  const show = (nextMode: RollDieMode, nextFace: number | null): void => {
    const wakes = nextMode === 'ready' && mode !== 'ready';
    mode = nextMode;
    face = nextFace;
    if (wakes) spinSpeed = ROLL_DIE_WAKE_SPIN;
    root.setAttribute('class', `roll-die roll-die--${mode}`);
    if (mode === 'ready' && !reducedMotion.matches) {
      if (frame === 0) frame = requestAnimationFrame(step);
      return;
    }
    stop();
    drawStill();
  };

  // The button gets its size from the layout, after the first draw and on a change of layout.
  new ResizeObserver(() => {
    if (frame === 0) drawStill();
  }).observe(root);

  return {
    svg: root,
    show,
    label: (labels) => {
      solid.label(labels);
      if (frame === 0) drawStill();
    },
    excite: (next) => {
      excited = next;
    },
  };
};
